import { useCallback } from "react"
import { useParams, useNavigate, useLocation, Link } from "react-router-dom"
import { useTheme, Box } from "@mui/material"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useMap } from "hooks/map/useMap"
import { useOrdinalMap } from "hooks/ordinal"
import {
  useFlyToMap,
  useGetUserLocationView,
  useGetMapViewFromUrl,
} from "hooks/map"

import { pluralize } from "utils/string"

import { FooterAction } from "extensions/one-siam/components/domains/navigation/FooterAction"

import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { SearchIcon } from "extensions/one-siam/components/icons"
import { OrdinalSelector } from "extensions/one-siam/components/OrdinalSelector"
import { AppNavigation } from "extensions/one-siam/container/kiosk-v"
import { OrdinalSelectSplide } from "extensions/one-siam/components/OrdinalSelector/OrdinalSelectSplide"
import { useGetMapViewFromElectronConfig } from "extensions/one-siam/hooks"
import { SeeAndDoButton } from "extensions/one-siam/components/domains/see-and-do"
import { AppWeatherWidget } from "extensions/one-siam/components/domains/weather"

export const MapOrdinalKioskVPage = () => {
  const navigate = useNavigate()
  const { ordinal } = useParams()
  const { search: searchQueryString } = useLocation()
  const { t } = useTranslation()
  const { isMultiOrdinalVenue } = useVenue()
  const {
    viewingVenueId,
    viewingVenueView,
    viewingVenueLevels,
    quickSearchCategories,
  } = useMap()

  const parsedOrdinal = Number(ordinal)

  useOrdinalMap(parsedOrdinal)
  const userLocationView = useGetUserLocationView()
  const electronMapView = useGetMapViewFromElectronConfig(
    viewingVenueId,
    parsedOrdinal
  ) // for One Siam
  const { mapView } = useGetMapViewFromUrl()
  useFlyToMap(
    electronMapView || mapView || userLocationView || viewingVenueView
  )

  const theme = useTheme()
  const {
    actionButton,
    actionContainer,
    toolbarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    navigationWrapper,
    ordinalSelectMenu,
    searchBarContainer,
    mainMenuContainer,
    footerActionContainer,
    ordinalSelector,
    seeAndDoButtonContainer,
  } = theme.components[`AppMap`]?.styleOverrides || {}

  const onClickOrdinal = useCallback(
    (ordinal) => {
      navigate(
        `/maps/${
          ordinal !== null ? `ordinal/${ordinal}${searchQueryString}` : ""
        }`,
        { replace: true }
      )
    },
    [navigate, searchQueryString]
  )

  const handleClickQuickSearch = useCallback(
    (type, category) => {
      const pluralType = pluralize(type)
      navigate(
        `/maps/${pluralType}/${category}?from-ordinal=${ordinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, ordinal, viewingVenueId]
  )

  return (
    <>
      <AppWeatherWidget />
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            selectedOrdinal={parsedOrdinal}
            levels={viewingVenueLevels}
            onClickOrdinal={onClickOrdinal}
          />
        </Box>
      )}
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelectSplide
            selectedOrdinal={parsedOrdinal}
            levels={viewingVenueLevels}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}

      <Box sx={seeAndDoButtonContainer} className="hello">
        <SeeAndDoButton />
      </Box>

      <Box key="app-navigation" sx={navigationWrapper}>
        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
        <Box sx={toolbarContainer}>
          <Box sx={actionContainer}>
            <LanguageButton variant="contained" sx={actionButton} />
          </Box>
          <Box sx={searchBarContainer}>
            <SearchToggleButton
              icon={SearchIcon}
              placeholder={t("Search shops and more...")}
              LinkComponent={Link}
              to="/search"
            />
          </Box>
        </Box>
        <Box sx={mainMenuContainer}>
          <AppNavigation />
          <FooterAction sx={footerActionContainer} />
        </Box>
      </Box>
    </>
  )
}
